import { useEffect, useRef, useState } from "react";
import { getPivotRollupStatus } from "../../services/pivot";
import { PivotDataSource } from "../../types/pivot.type";
import { shouldWatchRollupRebuild } from "./pivotDataSource";

/** Suggested by the backend: the status query is one lock scan and one indexed count. */
const POLL_INTERVAL_MS = 2500;

/**
 * Ten minutes. A single branch rebuilds in ~15s and all 27 branches in ~7 minutes, so this
 * clears the slowest real run with room to spare. Past it the watch simply stops: the
 * banner stays up saying the numbers are live and current, which is still true.
 */
const MAX_WATCH_MS = 10 * 60 * 1000;

/**
 * A finished rebuild should leave the next response on the rollup path. If it does not —
 * a rebuild that keeps failing, a gap outside the trailing window it covers — refetching
 * forever would be a slow request loop nobody asked for. Two rounds per scope, then the
 * page settles for the live numbers it already has.
 */
const MAX_AUTO_REFETCH = 2;

export interface PivotRollupWatch {
  /** 0–100 while the backend can measure it; `null` until the first poll answers. */
  percent: number | null;
  etaSeconds: number | null;
}

/**
 * Follows a background rollup rebuild and refetches the pivot when it ends.
 *
 * The pivot response only says what *this request* asked for: `STARTED` can mean another
 * task already held the cluster-wide lock and this attempt was deflected onto its run.
 * `/pivot/rollup/status` is the only thing that knows whether one is actually running, and
 * `refreshing: false` is the only end condition — the progress numbers are `null`, not 0,
 * whenever nothing is running, precisely so a finished run cannot leave a bar frozen at the
 * last run's total.
 */
export const usePivotRollupWatch = ({
  dataSource,
  branchCode,
  resetKey,
  onFinished,
}: {
  dataSource: PivotDataSource | null | undefined;
  branchCode?: string;
  /** Scope identity. Changing it restores the auto-refetch budget. */
  resetKey: string;
  onFinished: () => void;
}): PivotRollupWatch | null => {
  const [watch, setWatch] = useState<PivotRollupWatch | null>(null);

  // Kept in refs so a new callback identity on every render cannot restart the poll loop.
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const budgetRef = useRef(MAX_AUTO_REFETCH);
  const lastResetKey = useRef(resetKey);
  if (lastResetKey.current !== resetKey) {
    lastResetKey.current = resetKey;
    budgetRef.current = MAX_AUTO_REFETCH;
  }

  const watching = shouldWatchRollupRebuild(dataSource);
  const initialEta = dataSource?.refresh?.retry_after_seconds ?? null;

  useEffect(() => {
    if (!watching || budgetRef.current <= 0) {
      setWatch(null);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Show the backend's own estimate straight away; the first poll is 2.5s out.
    setWatch({ percent: null, etaSeconds: initialEta });

    const deadline = Date.now() + MAX_WATCH_MS;

    const tick = async () => {
      try {
        const { data } = await getPivotRollupStatus(
          branchCode ? { branch_code: branchCode } : {},
        );
        if (cancelled) return;

        // `refreshing: false` is the only end condition — an envelope with no payload says
        // nothing either way, so wait for the next tick rather than call the rebuild done.
        const status = data.data;
        if (status) {
          if (!status.refreshing) {
            setWatch(null);
            budgetRef.current -= 1;
            onFinishedRef.current();
            return;
          }
          setWatch({
            percent: status.percent,
            etaSeconds: status.estimated_seconds_remaining,
          });
        }
      } catch {
        // A failed poll says nothing about the rebuild, and the banner it drives is already
        // on screen. Keep the last known progress and try again on the next tick.
        if (cancelled) return;
      }

      if (Date.now() >= deadline) {
        setWatch(null);
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // `initialEta` is only the seed for the first render of the bar; re-running the effect
    // for it would restart the poll loop on every response that carries the same estimate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watching, branchCode, resetKey, dataSource]);

  return watch;
};
